import React, { useState } from 'react';
import { useFirebaseAuth } from '../context/FirebaseAuthContext';
import './Login.css';

// Feature flag for phone auth - enable in production
const PHONE_AUTH_ENABLED = process.env.NODE_ENV === 'production' || process.env.REACT_APP_ENABLE_PHONE_AUTH === 'true';

const PROVIDERS = [
  { key: 'google', label: 'Google', icon: '🔑' },
  { key: 'email', label: 'Email', icon: '✉️' },
  ...(PHONE_AUTH_ENABLED ? [{ key: 'phone', label: 'Phone', icon: '📱' }] : []),
];

const Login = () => {
  const {
    login,
    user,
    loginWithEmail,
    registerWithEmail,
    sendPhoneCode,
    verifyPhoneCode,
    setupRecaptcha,
    clearRecaptcha,
    confirmationResult,
    loading,
    phoneAuthEnabled,
  } = useFirebaseAuth();

  const [provider, setProvider] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isRegister, setIsRegister] = useState(false);
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpError, setOtpError] = useState('');

  // Email/Password handlers
  const handleEmailAuth = async (e) => {
    e.preventDefault();
    setEmailError('');
    try {
      if (isRegister) {
        await registerWithEmail(email, password);
      } else {
        await loginWithEmail(email, password);
      }
    } catch (err) {
      setEmailError(err.message);
    }
  };

  // Phone handlers
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setPhoneError('');
    setOtpError('');
    
    if (!phoneAuthEnabled || !sendPhoneCode || !setupRecaptcha) {
      setPhoneError('Phone authentication is not available in this environment.');
      return;
    }
    
    try {
      // Ensure phone number is in proper format
      let formattedPhone = phone.trim();
      if (!formattedPhone.startsWith('+')) {
        formattedPhone = '+1' + formattedPhone.replace(/^\+?1?/, '');
      }
      
      const verifier = setupRecaptcha('recaptcha-container');
      await sendPhoneCode(formattedPhone, verifier);
      setOtpSent(true);
    } catch (err) {
      console.error('Phone auth error:', err);
      setPhoneError(err.message || 'Failed to send OTP. Please try again.');
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setOtpError('');
    
    if (!phoneAuthEnabled || !verifyPhoneCode) {
      setOtpError('Phone authentication is not available in this environment.');
      return;
    }
    
    try {
      await verifyPhoneCode(otp);
    } catch (err) {
      setOtpError(err.message);
    }
  };

  if (user) {
    return (
      <div className="login-container">
        <div className="welcome-card">
          <div className="user-avatar">
            {user?.photoURL ? (
              <img src={user.photoURL} alt={user.displayName} />
            ) : (
              <div className="avatar-placeholder">
                {user?.displayName?.charAt(0) || user?.email?.charAt(0) || 'U'}
              </div>
            )}
          </div>
          <h1>Welcome, {user?.displayName || user?.email}!</h1>
          <p>You're successfully logged in.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>Chalkolate</h1>
          <p>Have fun building life skills</p>
        </div>
        {!provider ? (
          <div className="provider-select">
            <h2>Sign in with</h2>
            <div className="provider-buttons">
              {PROVIDERS.map((p) => (
                <button
                  key={p.key}
                  className="provider-btn"
                  onClick={() => setProvider(p.key)}
                  disabled={loading}
                >
                  <span className="provider-icon">{p.icon}</span>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="login-content">
            <button className="back-btn" onClick={() => {
              setProvider(null);
              setOtpSent(false);
              setPhoneError('');
              setOtpError('');
              setEmailError('');
              if (clearRecaptcha) clearRecaptcha();
            }}>&larr; Back</button>
            {provider === 'google' && (
              <button className="login-button" onClick={login} disabled={loading}>
                <span className="button-icon">🔑</span>
                Sign In with Google
              </button>
            )}
            {provider === 'email' && (
              <form className="login-form" onSubmit={handleEmailAuth}>
                <input
                  type="email"
                  placeholder="Email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
                <input
                  type="password"
                  placeholder="Password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
                {emailError && <div className="error-message">{emailError}</div>}
                <button className="login-button" type="submit" disabled={loading}>
                  {isRegister ? 'Register' : 'Login'}
                </button>
                <div className="login-toggle">
                  {isRegister ? (
                    <span>Already have an account? <button type="button" onClick={() => setIsRegister(false)}>Login</button></span>
                  ) : (
                    <span>Don&apos;t have an account? <button type="button" onClick={() => setIsRegister(true)}>Register</button></span>
                  )}
                </div>
              </form>
            )}
            {provider === 'phone' && (
              <form className="login-form" onSubmit={otpSent ? handleVerifyOtp : handleSendOtp}>
                <div id="recaptcha-container" />
                {!otpSent ? (
                  <>
                    <input
                      type="tel"
                      placeholder="Phone number (e.g. +1234567890)"
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      required
                    />
                    {phoneError && <div className="error-message">{phoneError}</div>}
                    <button className="login-button" type="submit" disabled={loading}>
                      Send OTP
                    </button>
                  </>
                ) : (
                  <>
                    <input
                      type="text"
                      placeholder="Enter OTP"
                      value={otp}
                      onChange={e => setOtp(e.target.value)}
                      required
                    />
                    {otpError && <div className="error-message">{otpError}</div>}
                    <button className="login-button" type="submit" disabled={loading}>
                      Verify OTP
                    </button>
                  </>
                )}
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Login; 